class BundleSubscriptionWidget {
  constructor() {
    this.attributes = {
      widget: 'data-bundle-subscription-widget',
      sellingPlanIdInput: 'data-bundle-subscription-selling-plan-id-input',
      sellingPlansContainer: 'data-bundle-subscription-selling-plans-container',
      sellingPlanGroup: 'data-bundle-subscription-selling-plan-group',
      sellingPlanGroupInput: 'data-bundle-subscription-selling-plan-group-input',
      sellingPlan: 'data-bundle-subscription-selling-plan',
      sellingPlanInput: 'data-bundle-subscription-selling-plan-input',
      productJson: 'data-bundle-subscription-product-json',
      groupDiscountSummary: 'data-bundle-subscription-group-discount-summary',
      perDeliveryPrice: 'data-bundle-subscription-per-delivery-price',
      moneyFormat: 'data-bundle-subscription-money-format',
      pageTemplate: 'data-bundle-subscription-page-template',
    };

    this.selectors = {
      productForm: 'form[action="/cart/add"]',
      variantIdInput: '[name="id"]',
      variantSelectors: ['.single-option-selector', 'select[name=id]', 'input[name=id]'],
    };

    Object.entries(this.attributes).forEach(([key, attribute]) => {
      this.selectors[key] = `[${attribute}]`;
    });

    this.hiddenClass = 'bundle-subscription-widget--hidden';

    this.products = {};
    this.variants = {};
    this.sellingPlanGroups = {};
    this.pageTemplate = '';
  }

  init() {
    if (!document.querySelector(this.selectors.widget)) return;

    this.parsePageTemplate();
    this.parseProductJson();
    this.addVariantChangeListener();
    this.addWidgetChangeListeners();

    document.querySelectorAll(this.selectors.widget).forEach((widget) => {
      this.renderPrices(widget);
      this.renderGroupDiscountSummary(widget);
    });

    window.addEventListener('pageshow', () => this.syncAllVisuallySelected());
  }

  addWidgetChangeListeners() {
    document.querySelectorAll(this.selectors.widget).forEach((widget) => {
      widget.querySelectorAll(this.selectors.sellingPlanGroupInput).forEach((input) => {
        input.addEventListener('change', (event) => this.handleSellingPlanGroupChange(event));
      });
      widget.querySelectorAll(this.selectors.sellingPlanInput).forEach((input) => {
        input.addEventListener('change', (event) => this.handleSellingPlanChange(event));
      });
    });
  }

  /**
   * The browser restores form state on back/forward navigation without firing
   * change events, so re-sync the hidden input from the visually selected plan.
   */
  syncAllVisuallySelected() {
    document.querySelectorAll(this.selectors.widget).forEach((widget) => {
      const selectedGroup = widget.querySelector(`${this.selectors.sellingPlanGroupInput}:checked`);
      if (selectedGroup) selectedGroup.dispatchEvent(new Event('change'));
    });
  }

  addVariantChangeListener() {
    const variantSelectors = document.querySelectorAll(this.selectors.variantSelectors.join());

    variantSelectors.forEach((select) => {
      select.addEventListener('change', (event) => {
        const productForm = event.target.closest(this.selectors.productForm);
        const widget = productForm && productForm.querySelector(this.selectors.widget);
        if (!widget) return;

        // Allow the variant change to propagate to input[name=id] first.
        setTimeout(() => {
          this.renderPrices(widget);
          this.renderGroupDiscountSummary(widget);
        }, 100);
      });
    });
  }

  parsePageTemplate() {
    const pageTemplateInput = document.querySelector(this.selectors.pageTemplate);
    if (pageTemplateInput) this.pageTemplate = pageTemplateInput.value;
  }

  parseProductJson() {
    document.querySelectorAll(this.selectors.productJson).forEach((element) => {
      const product = JSON.parse(element.innerHTML);
      this.products[element.dataset.bundleSubscriptionProductId] = product;

      product.selling_plan_groups.forEach((group) => {
        this.sellingPlanGroups[group.id] = group;
      });

      product.variants.forEach((variant) => {
        this.variants[variant.id] = variant;
      });
    });
  }

  /** Display "price / delivery" for each plan label; re-run on variant change. */
  renderPrices(widget = document.querySelector(this.selectors.widget)) {
    const variantId = this.getVariantId(widget);
    if (!variantId) return;

    widget.querySelectorAll(this.selectors.sellingPlan).forEach((planElement) => {
      const sellingPlanId = planElement.dataset.bundleSubscriptionSellingPlanId;
      const allocation = this.getSellingPlanAllocation(variantId, sellingPlanId);
      const priceElement = planElement.querySelector(this.selectors.perDeliveryPrice);
      if (allocation && priceElement) {
        priceElement.innerHTML = this.formatPrice(allocation.per_delivery_price);
      }
    });
  }

  renderGroupDiscountSummary(widget) {
    const productJsonElement = widget.querySelector(this.selectors.productJson);
    if (!productJsonElement) return;

    const productId = productJsonElement.dataset.bundleSubscriptionProductId;
    const product = this.products[productId];
    if (!product) return;

    widget.querySelectorAll(this.selectors.sellingPlanGroup).forEach((groupElement) => {
      const groupId = groupElement.dataset.bundleSubscriptionSellingPlanGroupId;
      const group = product.selling_plan_groups.find((item) => item.id === groupId);
      if (!group) return;

      const discounts = group.selling_plans.map((plan) => {
        if (plan.price_adjustments.length === 0) return { value: 0, type: '' };
        return {
          value: plan.price_adjustments[0].value,
          type: plan.price_adjustments[0].value_type,
        };
      });

      const maxDiscount = discounts.reduce((a, b) => (a.value > b.value ? a : b));
      if (maxDiscount.value === 0) return;

      const isUpTo = discounts.some((discount) => discount.value !== maxDiscount.value);
      const summaryElement = groupElement.querySelector(this.selectors.groupDiscountSummary);
      if (!summaryElement) return;

      let summary = '(Save ';
      if (isUpTo) summary += 'up to ';
      if (maxDiscount.type === 'fixed_amount') {
        summary += this.formatPrice(maxDiscount.value);
      } else {
        summary += `${maxDiscount.value}%`;
      }
      summary += ')';

      summaryElement.innerHTML = summary;
    });
  }

  handleSellingPlanGroupChange(event) {
    const groupInput = event.target;
    const groupId = groupInput.value;
    const widget = groupInput.closest(this.selectors.widget);

    widget.querySelectorAll(this.selectors.sellingPlansContainer).forEach((container) => {
      const matches = container.dataset.bundleSubscriptionSellingPlanGroupId === groupId;
      container.classList.toggle(this.hiddenClass, !(matches && groupInput.checked));
    });

    if (groupId === 'once') {
      this.setSellingPlanIdInput(widget, '');
      return;
    }

    this.setSellingPlanIdInput(widget, this.getActiveSellingPlanId(widget, groupId));
  }

  handleSellingPlanChange(event) {
    const planInput = event.target;
    const widget = planInput.closest(this.selectors.widget);
    this.setSellingPlanIdInput(widget, planInput.value);
  }

  setSellingPlanIdInput(widget, sellingPlanId) {
    const input = widget.querySelector(this.selectors.sellingPlanIdInput);
    input.value = sellingPlanId;

    if (this.pageTemplate.includes('product')) {
      this.updateHistoryState(this.getVariantId(widget), sellingPlanId);
    }
  }

  getVariantId(widget) {
    const productForm = widget.closest(this.selectors.productForm);
    if (!productForm) return null;
    const variantIdInput = productForm.querySelector(this.selectors.variantIdInput);
    return variantIdInput ? variantIdInput.value : null;
  }

  getActiveSellingPlanId(widget, groupId) {
    const activePlan = widget.querySelector(`input[name=bundle-subscription-selling-plan-${groupId}]:checked`);
    return activePlan ? activePlan.value : '';
  }

  updateHistoryState(variantId, sellingPlanId) {
    if (!history.replaceState || !variantId) return;

    const params = new URLSearchParams();
    if (sellingPlanId) params.set('selling_plan', sellingPlanId);
    params.set('variant', variantId);

    const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?${params}`;
    window.history.replaceState({ path: newUrl }, '', newUrl);
  }

  /** How a selling plan applies to a specific variant. */
  getSellingPlanAllocation(variantId, sellingPlanId) {
    const variant = this.variants[variantId];
    if (!variant) return null;
    return variant.selling_plan_allocations.find(
      (allocation) => `${allocation.selling_plan_id}` === sellingPlanId
    );
  }

  formatPrice(cents, format) {
    const moneyElement = document.querySelector(this.selectors.moneyFormat);
    const moneyFormat = moneyElement ? moneyElement.getAttribute(this.attributes.moneyFormat) : null;

    let amount = cents;
    if (typeof amount === 'string') amount = amount.replace('.', '');

    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || moneyFormat || this.getThemeMoneyFormat();

    const formatWithDelimiters = (number, precision, thousands = ',', decimal = '.') => {
      if (isNaN(number) || number === null) return 0;
      const fixed = (number / 100.0).toFixed(precision);
      const parts = fixed.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const remainder = parts[1] ? decimal + parts[1] : '';
      return dollars + remainder;
    };

    let value = '';
    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(amount, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(amount, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(amount, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(amount, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(amount, 0, ' ');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(amount, 2, "'");
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }

  getThemeMoneyFormat() {
    if (typeof Shopify !== 'undefined' && typeof Shopify.money_format !== 'undefined') {
      return Shopify.money_format;
    }
    if (typeof theme !== 'undefined') {
      if (typeof theme.moneyFormat !== 'undefined') return theme.moneyFormat;
      if (theme.strings && typeof theme.strings.moneyFormat !== 'undefined') return theme.strings.moneyFormat;
      if (theme.shop && typeof theme.shop.money_format !== 'undefined') return theme.shop.money_format;
    }
    return '${{ amount }}';
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new BundleSubscriptionWidget().init();
});
